/* jslint node: true */

"use strict";

var core = require("./core"),
  assertion = require("./utils/assertion"),
  assert = assertion.assert,
  basic = require("./utils/basic"),
  getValueOrDefault = basic.getValueOrDefault,
  assertType = assertion.assertType,
  fullStackPhoneClient = require("fullstack-phone/client");

var PhoneHandler = function() {};

var messages = {
  // validation and parsing errors returned from fullstack-phone, map to a message to be returned (not thrown)
  errors: {
    PHONE_INVALID_COUNTRY_CODE:
      "The country calling code is invalid for the country",
    // note that fullstack-phone actually returns PHONE_INVALID_FOR_REGION,
    // which is mapped to PHONE_INVALID_FOR_COUNTRY in mapFullStackPhoneValidationErrors
    PHONE_INVALID_FOR_COUNTRY: "Phone number is invalid for the country",
    PHONE_NUMBER_TOO_LONG: "Phone number is too long",
    PHONE_NUMBER_TOO_SHORT: "Phone number is too short",

    // these are only returned by normalizePhoneNumber:
    PHONE_NOT_A_NUMBER: "The string supplied did not seem to be a phone number",
    PHONE_TOO_SHORT_AFTER_IDD: "Phone number is too short after IDD",
    PHONE_ALPHA_CHARACTERS: "Phone number should not contain alpha characters"
  }
};

var validNumberTypeStrings = [
  "FIXED_LINE",
  "MOBILE",
  "FIXED_LINE_OR_MOBILE",
  "TOLL_FREE",
  "PREMIUM_RATE",
  "SHARED_COST",
  "VOIP",
  "PERSONAL_NUMBER",
  "PAGER",
  "UAN",
  "VOICEMAIL"
];

// style constants
var styles = {
  national: "PHONENUMBER_FORMAT_NATIONAL",
  international: "PHONENUMBER_FORMAT_INTERNATIONAL",
  nationalObscured: "PHONENUMBER_FORMAT_NATIONAL_OBSCURED",
  e164: "PHONENUMBER_FORMAT_E164",
  rfc3966: "PHONENUMBER_FORMAT_RFC3966"
};

// populate handler prototype with style string constants
Object.keys(styles).forEach(function(style) {
  Object.defineProperty(PhoneHandler.prototype, styles[style], {
    get: function() {
      return style;
    },
    enumerable: true
  });
});

/**
 * @function
 * @name formatAsTyped
 * @memberof PhoneHandler
 * @param {string} phoneNumberString raw string of digits (including +) to format
 * @param {Object} options optional options object with country property (uses handler country as default)
 * @returns {string} partial/complete formatted phone number
 * @throws {Error} if parameters invalid
 *
 */
Object.defineProperty(PhoneHandler.prototype, "formatAsTyped", {
  value: function formatAsTyped(phoneNumberString, options) {
    assertType(phoneNumberString, "phoneNumber", ["string"]);
    options = getValueOrDefault(options, {});
    assertType(options, "options", ["plain object"]);

    // resolve regionCode alias and throw if regionCode is invalid or unsupported
    // use handler country by default if options.country is undefined
    var regionCode = validateAndResolveRegionAlias(this, options.country);

    var formatter = this.fullStackPhoneHandler.getAsYouTypeFormatter(
      regionCode
    );

    var output = "";

    // loop over phone number string and input digits one by one, then return final output
    for (var i = 0; i < phoneNumberString.length; i++) {
      output = formatter.inputDigit(phoneNumberString.charAt(i));
    }

    return output;
  },
  enumerable: true
});

/**
 * @function
 * @name getAsYouTypeFormatter
 * @memberof PhoneHandler
 * @param {Object} options optional options object with country property (uses handler country as default)
 * @returns {Object} AsYouTypeFormatter instantiated to country
 * @throws {Error} if parameter is invalid
 *
 * TODO: can AsYouTypeFormatter work with sub-region mapping?
 */
Object.defineProperty(PhoneHandler.prototype, "getAsYouTypeFormatter", {
  value: function getAsYouTypeFormatter(options) {
    options = getValueOrDefault(options, {});
    assertType(options, "options", ["plain object"]);

    // resolve regionCode alias and throw if regionCode is invalid or unsupported
    // use handler country by default if options.country is undefined
    var regionCode = validateAndResolveRegionAlias(this, options.country);

    // protected variables (accessible only by functions below)
    var formatter = this.fullStackPhoneHandler.getAsYouTypeFormatter(
        regionCode
      ),
      formattedOutput = "",
      rawInput = "";

    // return an object with AsYouTypeFormatter methods
    return {
      // get formatted output so far
      getFormattedOutput: function getFormattedOutput() {
        return formattedOutput;
      },

      // expose inputDigit from AsYouTypeFormatter as 'pushAndFormat'
      pushAndFormat: function pushAndFormat(digit) {
        rawInput += digit;
        formattedOutput = formatter.inputDigit(digit);
        return formattedOutput;
      },

      // expose clear from AsYouTypeFormatter
      clear: function clear() {
        rawInput = "";
        formattedOutput = "";
        return formatter.clear();
      },

      // remove last inputted character and re-run through formatter
      popAndFormat: function popAndFormat() {
        if (!rawInput) {
          // if already empty, do nothing
          return "";
        } else if (rawInput.length === 1) {
          // if only one character, delete it by just clearing the formatter
          this.clear();
          return "";
        }

        var newRawInput = rawInput.substring(0, rawInput.length - 1); // trim off final character
        this.clear(); // clear formatter

        // loop over chars and re-run them through inputDigit, then return final formatted output
        var output;
        newRawInput.split("").forEach(function(digit) {
          output = this.pushAndFormat(digit);
        }, this); // pass reference to 'this' inside forEach
        return output;
      }
    };
  },
  enumerable: false // hide for now
});

/**
 * @function
 * @name formatPhoneNumber
 * @memberof PhoneHandler
 * @param {Object} phoneObj
 * @param {Object} options - {style : 'international', 'national', 'e164' or 'rfc3699'} (optional)
 * @return {string} formatted phone number
 * @throws {Error} exception (see master list) if problems with function parameters or metadata
 */
Object.defineProperty(PhoneHandler.prototype, "formatPhoneNumber", {
  value: function formatPhoneNumber(phoneObj, options) {
    assertType(phoneObj, "phoneObj", ["plain object"]);
    options = getValueOrDefault(options, {});
    assertType(options, "options", ["plain object"]);

    // use national style by default if options.style is undefined
    var style = getValueOrDefault(
      options.style,
      PhoneHandler.prototype.PHONENUMBER_FORMAT_NATIONAL
    );

    assertPhoneObjType(phoneObj); // validate details of phoneObj parameter

    // validate style option
    assertion.assertRange(
      style,
      styles,
      "Invalid value for options.style: {style}",
      { style: style }
    );

    // special handling for obscured style
    if (style === PhoneHandler.prototype.PHONENUMBER_FORMAT_NATIONAL_OBSCURED) {
      return formatNationalObscured(this, phoneObj);
    }

    // otherwise pass to fullStackPhoneHandler
    return this.fullStackPhoneHandler.formatPhoneNumber(phoneObj, {
      style: style
    });
  },
  enumerable: true
});

/**
 * @function
 * @name validatePhoneNumber
 * @memberof PhoneHandler
 * @param {Object} phoneObj
 * @param {Object} options optional options object with country property (uses handler country as default)
 * @return {object} if valid, return empty object
 *                  if invalid, return object with errors
 * @throws {Error} if parameters are invalid
 */
Object.defineProperty(PhoneHandler.prototype, "validatePhoneNumber", {
  value: function validatePhoneNumber(phoneObj, options) {
    assertType(phoneObj, "phoneObj", ["plain object"]);
    options = getValueOrDefault(options, {});
    assertType(options, "options", ["plain object"]);

    assertPhoneObjType(phoneObj); // validate details of phoneObj parameter

    // resolve regionCode alias and throw if regionCode is invalid or unsupported
    // use handler country by default if options.country is undefined
    var regionCode = validateAndResolveRegionAlias(this, options.country);

    var errorObj = {},
      result;

    result = this.fullStackPhoneHandler.validatePhoneNumber(
      phoneObj,
      regionCode
    );

    // boolean true means phone passed validation
    if (result === true) {
      return {};
    }

    // otherwise, validatePhoneNumber returned an Error object, because the phone failed validation for the main region

    // try validating against sub-regions
    // if any of those pass, consider the phone valid and return {}
    // if they all fail, just return the original error from the main region

    // get array of sub-regions associated with this region code (e.g. GG, IM, JE for GB)
    var subRegions = getSubRegions(this, regionCode);
    if (subRegions) {
      for (var i = 0; i < subRegions.length; i++) {
        var subRegionResult = this.fullStackPhoneHandler.validatePhoneNumber(
          phoneObj,
          subRegions[i]
        );
        if (subRegionResult === true) {
          // phone validation passed for this subregion
          return {};
        }
      }
    }

    // no sub-regions passed

    // map the fullstack-phone Error object to a griffin phone message and return
    core.addError(
      errorObj,
      messages,
      mapFullStackPhoneValidationErrors(result.message)
    );
    return errorObj;
  },
  enumerable: true
});

/**
 * @function
 * @name normalizePhoneNumber
 * @memberof PhoneHandler
 * @param {string} phoneNumberToParse
 * @param {Object} options (optional), with country property
 * @return {Object} if no errors, return normalized phoneObj
 *                  if parse or validation failed, return new object with errors array
 * @throws {Error} if parameters invalid, or if parse encountered an exception (which should never happen)
 *
 */
Object.defineProperty(PhoneHandler.prototype, "normalizePhoneNumber", {
  value: function normalizePhoneNumber(phoneNumberToParse, options) {
    assertType(phoneNumberToParse, "phoneNumber", ["string"]);
    options = getValueOrDefault(options, {});
    assertType(options, "options", ["plain object"]);

    // resolve regionCode alias and throw if regionCode is invalid or unsupported
    // use handler country by default if options.country is undefined
    var regionCode = validateAndResolveRegionAlias(this, options.country);

    // try parse + validate against main region
    var parseValidateResult = parseAndValidate(
      this,
      phoneNumberToParse,
      regionCode
    );

    // if parse + validate for the main region passed, return the parsed phoneObj
    if (!parseValidateResult.errors) {
      return parseValidateResult;
    }

    // otherwise, parsing or validation failed for the main region represented by the regionCode

    // try parsing + validating against sub-regions
    // if any of those succeed, return the parsed phoneObj from that sub-region
    // if they all fail, just return the original error from the main region

    // get array of sub-regions associated with this region code (e.g. GG, IM, JE for GB)
    var subRegions = getSubRegions(this, regionCode);
    if (subRegions) {
      for (var i = 0; i < subRegions.length; i++) {
        var subRegionResult = parseAndValidate(
          this,
          phoneNumberToParse,
          subRegions[i]
        );
        if (!subRegionResult.errors) {
          // phone parsing + validation succeeded for this sub-region
          return subRegionResult;
        }
      }
    }

    // no sub-regions found, or none succeeded
    // so just return the original failure result from parse + validate against the main region
    return parseValidateResult;
  },
  enumerable: true
});

/**
 * Returns a sample phone number for the given region and type
 *
 * @function
 * @name getExamplePhoneNumber
 * @memberof PhoneHandler
 * @param {string} numberType - phone number type, ie. 'FIXED_LINE', 'MOBILE', 'FIXED_LINE_OR_MOBILE',
 *         'TOLL_FREE', 'PREMIUM_RATE', 'SHARED_COST', 'VOIP', 'PERSONAL_NUMBER', 'PAGER', 'UAN', 'VOICEMAIL', 'UNKNOWN'
 * @param {Object} options optional options object with country property (uses handler country as default)
 * @return {Object} sample phone number (as phone object)
 * @throws {Error} if parameters are invalid
 *
 */
Object.defineProperty(PhoneHandler.prototype, "getExamplePhoneNumber", {
  value: function getExamplePhoneNumber(numberType, options) {
    assertion.assertRange(
      numberType,
      validNumberTypeStrings,
      "Invalid value for number type: {type}",
      { type: numberType }
    );
    options = getValueOrDefault(options, {});
    assertType(options, "options", ["plain object"]);

    // resolve regionCode alias and throw if regionCode is invalid or unsupported
    // use handler country by default if options.country is undefined
    var regionCode = validateAndResolveRegionAlias(this, options.country);

    return this.fullStackPhoneHandler.getExampleNumberForType(
      numberType,
      regionCode
    );
  },
  enumerable: true
});

/**
 * HELPER FUNCTIONS
 */

/***
 * @private
 * @name      useMetadata
 * @param     {Object} handler the handler object in which to store the metadata
 * @param     {string} metadata metadata object to use
 * @returns   {object} this
 * @throws    {Error} if metadata is invalid
 */
function useMetadata(handler, metadata) {
  // TODO check for metadata format validity based on the phone metadata schema
  assertType(metadata.libphonenumber, "", ["plain object"], {
    errorMessage: core.generalMessages.errors.METADATA_INVALID
  });
  assertType(metadata.regions, "", ["array"], {
    errorMessage: core.generalMessages.errors.METADATA_INVALID
  });
  assertType(metadata.regionGroups, "", ["plain object"], {
    errorMessage: core.generalMessages.errors.METADATA_INVALID
  });
  assertType(metadata.country, "", ["string"], {
    errorMessage: core.generalMessages.errors.METADATA_INVALID
  });

  // initially pass libphonenumber metadata down to phoneClient.createPhoneHandler
  // to catch any problems
  var fullStackPhoneHandler;
  try {
    fullStackPhoneHandler = fullStackPhoneClient.createPhoneHandler(
      metadata.libphonenumber
    );
  } catch (e) {
    return core.throwError(core.generalMessages.errors.METADATA_INVALID);
  }

  // store the fullStackPhoneHandler as a property of this phone handler
  Object.defineProperty(handler, "fullStackPhoneHandler", {
    value: fullStackPhoneHandler
  });

  // use Object.defineProperty to make metadata read-only and somewhat hidden
  Object.defineProperty(handler, "metadata", {
    value: {
      libphonenumber: metadata.libphonenumber,
      regions: metadata.regions,
      regionGroups: metadata.regionGroups
      // omit metadata.country, and store that as a property of the handler itself
    }
  });

  // store the handler country
  Object.defineProperty(handler, "country", {
    value: metadata.country
  });
}

/***
 * @private
 * @name      getMetadata
 * @param     {Object} handler the handler object
 * @param     {string} section section of metadata to return
 * @returns   {Object} metadata or section thereof
 * @throws    {Error} if metadata or section thereof not found
 */
function getMetadata(handler, section) {
  if (section) {
    // if section of metadata requested, return that property
    assert(
      handler.metadata[section],
      core.generalMessages.errors.METADATA_REQUIRED
    );

    return handler.metadata[section];
  }
  return handler.metadata; // otherwise return all metadata
}

/***
 * @private
 * @name      getSubRegions
 * @param     {Object} handler
 * @param     {string} regionCode
 * @returns   {Array|undefined} array of sub-regions associated with this region code
 *                              or undefined if no sub-regions
 *
 */
function getSubRegions(handler, regionCode) {
  return getMetadata(handler, "regionGroups")[regionCode];
}

/***
 * @private
 * @name      validateAndResolveRegionAlias
 * @param     {Object} handler
 * @param     {string|undefined} regionCode
 * @returns   {string} regionCode or resolved value if it's an alias (C2-->CN, UK--GB)
 * @throws    {Error} if regionCode is invalid or unsupported
 *
 */
function validateAndResolveRegionAlias(handler, regionCode) {
  // regionCode should be non-empty string or undefined
  assertNonEmptyStringOrUndef(regionCode, "options.country");

  // resolve alias (if regionCode is undefined, handler country is used)
  var regionAliasResolved = core.getCountry(handler, regionCode), // maps C2-->CN and UK-->GB if necessary
    supportedRegions = getMetadata(handler, "regions");

  // throw if not listed in supported regions
  assertion.assertRange(
    regionAliasResolved,
    supportedRegions,
    "No metadata loaded for given country: {country}",
    { country: regionCode }
  );

  return regionAliasResolved;
}

/**
 * @private
 * @name assertNonEmptyStringOrUndef
 * @param {*} parameter
 * @param {string} parameterName
 * @return undefined if parameter is a non-empty string or is undefined
 * @throws {Error} if parameter is anything other than this: non-empty string or undefined
 */
function assertNonEmptyStringOrUndef(parameter, parameterName) {
  assertType(parameter, parameterName, [], {
    condition:
      (typeof parameter === "string" && parameter !== "") ||
      parameter === undefined,
    errorMessage:
      "Parameter `{parameterName}` must be a non-empty string, not {parameterType} `{{parameterValue}}`"
  });
}

/**
 * @private
 * @name formatNationalObscured
 * @param {Object} handler the current handler (to pass on to phoneUtilWrapper)
 * @param {Object} phoneObj
 * @return {string} phone string formatted in national obscured style
 *
 * Helper function for special handling of national obscured formatting
 */
function formatNationalObscured(handler, phoneObj) {
  // start by formatting as NATIONAL
  var formattedPhoneNumber = handler.fullStackPhoneHandler.formatPhoneNumber(
    phoneObj,
    { style: PhoneHandler.prototype.PHONENUMBER_FORMAT_NATIONAL }
  );
  var regex = /\d/g, // if the related character is a 'digit'
    digits = formattedPhoneNumber.match(regex) || [];

  var inivisibleDigitsLength = digits.length / 2, // number of characters to hide, which is number of digits / 2, e.g. for (415) 815-9546 is 5
    firstVisibleDigitsLength =
      1 + digits.length - phoneObj.nationalNumber.length, // how many of the first digits is visible
    digitCount = 0; // we are going to call 'replace', passing a callback function. digitCount is incremented inside the function each time there is a regex match (digit)
  formattedPhoneNumber = formattedPhoneNumber.replace(regex, function(digit) {
    digitCount++;
    // show the first 'visible' digits, then hide the next ones so we have half of the digits as hidden
    return digitCount <= firstVisibleDigitsLength ||
      digitCount - firstVisibleDigitsLength > inivisibleDigitsLength
      ? digit
      : "*";
  });
  return formattedPhoneNumber;
}

/**
 * @private
 * @name parseAndValidate
 * @param {Object} handler the current handler (to pass on to phoneUtilWrapper)
 * @param {string} phoneNumberToParse phone string
 * @param {string} regionCode region code against which to parse & validate
 * @return {Object} if no errors, return normalized phoneObj
 *                  if parse or validation failed, return new object with errors array
 *
 * Helper function to parse + validate a phone string against a single libphonenumber region
 * normalizePhoneNumber calls this while looping over subregions
 */
function parseAndValidate(handler, phoneNumberToParse, regionCode) {
  var errorObj = {};

  // check for alpha characters other than initial "tel:" and return error if found
  // cf. https://github.paypal.com/Globalization-R/Griffin-Design/wiki/normalizePhoneNumber-leniency-issue
  if (hasAlphaCharacters(phoneNumberToParse)) {
    core.addError(errorObj, messages, "PHONE_ALPHA_CHARACTERS");
    return errorObj;
  }

  var parseResult = handler.fullStackPhoneHandler.parsePhoneNumber(
    phoneNumberToParse,
    regionCode
  );

  // if parse returned an Error object for the region, report the error
  if (parseResult instanceof Error) {
    // map the error message from phoneUtil (e.g. 'PHONE_NOT_A_NUMBER') to a phone module error
    core.addError(errorObj, messages, parseResult.message);
    return errorObj;
  }

  // otherwise parseResult is a phoneObj, so pass it through validate
  var validationResult = handler.fullStackPhoneHandler.validatePhoneNumber(
    parseResult,
    regionCode
  );

  // if validation passed, return the parsed phoneObj
  if (validationResult === true) {
    return parseResult;
  }

  // otherwise, validation failed
  // map the phoneUtil.validatePhoneNumber Error object to a griffin phone message and return
  core.addError(
    errorObj,
    messages,
    mapFullStackPhoneValidationErrors(validationResult.message)
  );
  return errorObj;
}

/**
 * @private
 * @name hasAlphaCharacters
 * @param {string} phoneString phone number string to test
 * @return {boolean} true if phoneString has alpha characters other than initial "tel:"
 *                   false if phoneString has no disallowed alpha characters
 */
function hasAlphaCharacters(phoneString) {
  var stringWithoutInitialTel = phoneString.replace(/^\s*tel:/i, ""); // strip off initial "tel:" (if it exists)
  return Boolean(stringWithoutInitialTel.match(/[a-zA-Z]/)); // return true if remaining string has alpha characters
}

/**
 * Given the message property of an error object from fullstack-phone, return the message key we want to map it to.
 * This simply maps PHONE_INVALID_FOR_REGION to PHONE_INVALID_FOR_COUNTRY.
 *
 * (Explanation: fullstack-phone uses PHONE_INVALID_FOR_REGION because that's more accurate, but Griffin uses
 * PHONE_INVALID_FOR_COUNTRY because we expose the region code property as 'country')
 *
 * @param {string} message
 * @return {string}
 */
function mapFullStackPhoneValidationErrors(message) {
  var messageMap = {
    PHONE_NUMBER_INVALID_LENGTH: "PHONE_NUMBER_TOO_LONG", // map new enum back to TOO_LONG for backward compatibility
    PHONE_INVALID_FOR_REGION: "PHONE_INVALID_FOR_COUNTRY"
  };
  return messageMap[message] || message;
}

/**
 * @private
 * @name assertPhoneObjType
 * @param {Object} phoneObj the object whose properties to test (assumes it's an object)
 * @return {undefined} if phoneObj type is valid
 * @throws {Error} if phoneObj type is invalid
 * Throw if phoneObj object is invalid (missing properties, properties are incorrect type, etc.)
 */
function assertPhoneObjType(phoneObj) {
  // Check well-formedness of countryCode (should be integer or string representation thereof)
  assertCanonicalInteger(phoneObj.countryCode, "phoneObj.countryCode");

  // Check well-formedness of nationalNumber (should be integer or string representation thereof)
  assertCanonicalInteger(phoneObj.nationalNumber, "phoneObj.nationalNumber");

  // Check well-formedness of extension (if provided, should be string or number)
  if (phoneObj.extension !== undefined) {
    // extension is optional
    assertType(phoneObj.extension, "phoneObj.extension", ["string", "number"]);
  }
}

/**
 * @private
 * @name assertCanonicalInteger
 * @param {*} parameter
 * @param {string} parameterName
 * @return undefined if parameter is an integer number of string representation of an integer
 * @throws {Error} if parameter fails to meet those conditions (including if empty string)
 */
function assertCanonicalInteger(parameter, parameterName) {
  // ensure that parameter is an integer or a string representation of an integer
  // and disallow empty strings
  assertType(parameter, parameterName, [], {
    condition:
      parameter !== "" &&
      (typeof parameter === "string" || typeof parameter === "number") &&
      isInteger(Number(parameter)),
    errorMessage:
      'Parameter `{parameterName}` must be an integer or a string representation of an integer (e.g, `"12345"`), not {parameterType} `{{parameterValue}}`'
  });
}

/**
 * @private
 * Return true if given param is an integer
 */
function isInteger(x) {
  return Math.round(x) === x;
}

// export is a factory function to return instantiated module for each metadata bundle
module.exports = {
  createHandler: function createHandler(metadata) {
    // ensure metadata is a plain object and follows the expected schema down to the phone level
    assertType(metadata, "metadata", ["plain object"]);
    assertType(metadata.griffin, "", ["plain object"], {
      errorMessage: core.generalMessages.errors.METADATA_INVALID
    });
    assertType(metadata.griffin.phone, "", ["plain object"], {
      errorMessage: core.generalMessages.errors.METADATA_INVALID
    });

    var handler = new PhoneHandler();
    useMetadata(handler, metadata.griffin.phone);
    return handler;
  }
};



//////////////////
// WEBPACK FOOTER
// /donatenodeweb-ci-2793/workspace/donate-gro-release/~/griffin-paypal/lib/client/phone.js
// module id = 253
// module chunks = 4 5 6 7 8 9 10